// SIP Trunking page content. Based on the CLI Secure SIP trunk page (same platform), in VOIP2 branding.
const M = '/media/sip';

export const SIP_TRUNK = {
  hero: {
    title: 'SIP trunking that',
    highlight: 'cuts call costs',
    after: ' and scales on demand',
    sub: 'Connect the phone system you already run to the VOIP2 network over the internet. Keep your numbers, add channels in minutes and stay online through any outage.',
    image: `${M}/hero.webp`,
    bullets: ['Keep your numbers', 'Elastic channels', 'Built-in failover', 'Works with any PBX'],
  },

  trust: [
    { value: 'Lower', label: 'line and call costs than ISDN' },
    { value: '99.99%', label: 'network availability SLA' },
    { value: 'Days', label: 'to port numbers, not weeks' },
    { value: '140+', label: 'countries for local numbers' },
  ],

  intro: {
    title: 'Your phone lines, delivered over the internet',
    paragraphs: [
      'A SIP trunk replaces physical ISDN and PSTN lines with a secure virtual connection between your phone system and the public telephone network, carried over your internet connection or a dedicated circuit.',
      'There are no copper lines and no per-line rental. You pay for the number of concurrent calls (channels) you need, add more in minutes for busy periods, and route calls across sites and to mobiles.',
    ],
    bullets: ['No physical lines or line rental', 'One provider for numbers, calls and support', 'Route calls across every site', 'Extra capacity for peaks and campaigns'],
  },

  switchOff: {
    title: 'The PSTN and ISDN switch-off is coming',
    text: 'Openreach is retiring the UK’s analogue PSTN and ISDN network by 2027. After that, those lines stop working. SIP trunking is the direct replacement, and we handle the migration for you.',
  },

  platform: {
    title: 'Watch every channel from the portal',
    sub: 'Trunk settings, allowed IPs, failover and live traffic in one place. Click a screen for a closer look.',
    screens: [
      { img: `${M}/screen-settings.webp`, title: 'Trunk settings', body: 'Channels, authentication, codecs, allowed IPs and the failover plan for each trunk.' },
      { img: `${M}/screen-traffic.webp`, title: 'Live traffic', body: 'Active calls, ASR, ACD and concurrent channels in real time, with fraud alerts.' },
    ],
  },

  benefits: {
    title: 'What a business phone connection should be',
    items: [
      { icon: 'Wallet', title: 'Lower running costs', body: 'Drop line rental and cut call charges. Calls between your sites stay on-net and free.' },
      { icon: 'Gauge', title: 'Scale in minutes', body: 'Add or remove channels as your team grows or a campaign spikes. Never pay for idle capacity.' },
      { icon: 'RefreshCw', title: 'Business continuity', body: 'If a site or line goes down, calls reroute to another site, a mobile or voicemail.' },
      { icon: 'Route', title: 'Keep your numbers', body: 'Port your geographic and non-geographic numbers, or add local numbers in 140+ countries.' },
      { icon: 'Plug', title: 'Works with your PBX', body: '3CX, Asterisk, FreePBX, Cisco, Avaya, Yeastar and more, or Microsoft Teams via Direct Routing.' },
      { icon: 'ShieldCheck', title: 'Secure by design', body: 'TLS and SRTP encryption, fraud monitoring and per-trunk call limits protect you from toll fraud.' },
    ],
  },

  compare: {
    title: 'Traditional lines vs SIP trunking',
    head: ['', 'ISDN / PSTN', 'VOIP2 SIP trunk'],
    rows: [
      ['Line rental', 'Per physical line', 'None, virtual channels'],
      ['Scaling', 'Weeks, engineer visit', 'Minutes, self-service'],
      ['Inter-site calls', 'Charged per minute', 'On-net and free'],
      ['Disaster recovery', 'Tied to the building', 'Reroute anywhere instantly'],
      ['Number porting', 'Limited and slow', 'Keep every number'],
      ['Future', 'Retiring by 2027', 'The current standard'],
    ],
  },

  compatibility: {
    title: 'Connects to the phone system you already run',
    sub: 'Vendor-neutral SIP trunks that work with nearly any modern PBX, on-premise or in the cloud.',
    systems: ['3CX', 'Asterisk', 'FreePBX', 'Cisco', 'Avaya', 'Yeastar', 'Grandstream', 'Microsoft Teams Direct Routing'],
  },

  steps: {
    title: 'Migrated and managed, end to end',
    items: [
      { verb: 'Assess', body: 'We review your lines, numbers, call volumes and PBX, then size the right number of channels.' },
      { verb: 'Provision', body: 'We set up the trunk, port your numbers with no downtime and configure routing and failover.' },
      { verb: 'Configure', body: 'We connect and tune your PBX, apply security policies and test every call scenario.' },
      { verb: 'Go live', body: 'You cut over, and our UK team monitors quality, capacity and fraud around the clock.' },
    ],
  },

  faqs: [
    { q: 'What is a SIP trunk in simple terms?', a: 'A virtual phone line delivered over the internet. Your phone system connects to the telephone network digitally instead of over ISDN lines, so you make and receive normal calls and pay only for the concurrent channels you need.' },
    { q: 'Can I keep my existing phone numbers?', a: 'Yes. We port your geographic and non-geographic numbers to the trunk with no loss of service, and can add local numbers in 140+ countries.' },
    { q: 'Will it work with my phone system?', a: 'Almost certainly. Our SIP trunks work with the major on-premise and cloud PBX platforms, including 3CX, Asterisk, FreePBX, Cisco, Avaya and Yeastar, and with Microsoft Teams via Direct Routing.' },
    { q: 'How does SIP trunking save money?', a: 'You remove physical line rental, calls between your sites become free on-net traffic, and long-distance and international rates are usually lower. Channels scale up and down, so you stop paying for idle capacity.' },
    { q: 'What happens if my internet or a site goes down?', a: 'Calls reroute to a backup site, a mobile or voicemail according to the failover plan we set up with you, so you keep taking calls during an outage.' },
    { q: 'Do I need new hardware for the 2027 switch-off?', a: 'Not necessarily. An IP-capable PBX can usually connect to SIP straight away. If yours is ISDN-only, we will advise on the most cost-effective upgrade as part of the migration.' },
  ],

  channelOptions: ['Not sure yet', 'Up to 5', '6–20', '21–50', '50+'],

  related: [
    { label: 'Cloud PBX', to: '/products/cloud-pbx' },
    { label: 'DID International Numbers', to: '/phone-numbers/did-international' },
    { label: 'Toll-Free Numbers', to: '/phone-numbers/toll-free' },
  ],
};
